/**
 * MESP Mobile Bridge — Milestone 3 · Wrapper App (Feb 2026).
 *
 * This module is the SINGLE integration point between the web SPA and
 * the Capacitor runtime. It is safe to import from anywhere in the
 * React tree; on the browser side it becomes a set of no-ops.
 *
 * Responsibilities
 * ----------------
 * 1. Detect whether we are running inside the Capacitor Wrapper.
 * 2. Request push-notification permission and register the FCM token
 *    against the backend (`POST /api/device-tokens/register`).
 * 3. Listen for deep-links (`mesp://...` custom scheme + `https://...`
 *    Universal Links) and forward them to React Router.
 * 4. Persist the JWT inside the OS-encrypted Preferences store on
 *    native builds, and fall back to `localStorage` on the web.
 * 5. Revoke the device token on logout via
 *    `POST /api/device-tokens/revoke`.
 *
 * NON-goals
 * ---------
 *   • No business logic ever lives here.
 *   • No routing tables — the module dispatches to a caller-supplied
 *     `navigate` callback so React Router owns the destination.
 *   • No knowledge of the FCM error taxonomy — that stays on the server.
 */

import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '')}/api`
  : '/api';

let cachedCapacitor = null;
const foregroundPushSeen = new Map();
const LIVE_EMERGENCY_SOUNDS = {
  live_teacher_no_show: '/sounds/mesp_ring_long_v2.wav',
  live_teacher_left: '/sounds/mesp_melody_medium_v2.wav',
  live_class_no_students: '/sounds/mesp_chime_short_v2.wav',
};

/** Detects whether we are inside a Capacitor WebView.
 *  Falls back gracefully in web builds — the whole module is safe there.
 */
export const isNative = () => {
  try {
    if (cachedCapacitor === null) {
      cachedCapacitor = typeof window !== 'undefined'
        && window.Capacitor
        && typeof window.Capacitor.isNativePlatform === 'function'
        && window.Capacitor.isNativePlatform();
    }
    return !!cachedCapacitor;
  } catch {
    return false;
  }
};

/** Lazy plugin import — safe from the browser (returns null) and safe
 *  from the Capacitor WebView.
 *
 *  Native path (fix, 2026-07-21): the frontend `package.json` does not
 *  depend on any `@capacitor/*` package (only the wrapper does), so a
 *  raw dynamic `import('@capacitor/…')` reaches for a network module
 *  that doesn't exist and silently fails — which is why
 *  `registerPushToken` was returning `plugin_missing` on device and
 *  no FCM token was ever uploaded to the backend.
 *
 *  On native, Capacitor's Android runtime already injects
 *  `window.Capacitor.Plugins.<PluginName>` at WebView load — the same
 *  proxy object the npm package would have exposed via
 *  `registerPlugin(name)`. We therefore prefer that global and shape
 *  the return value identically to the npm package's default export
 *  (`{ <PluginName>: <proxy> }`) so existing call-sites stay untouched.
 *  The dynamic-import fallback is preserved for future compatibility
 *  (e.g. if a plugin ever needs pure-JS symbols not exposed by the
 *  native bridge). */
const _NPM_TO_PLUGIN = {
  '@capacitor/push-notifications': 'PushNotifications',
  '@capacitor/app':                'App',
  '@capacitor/device':             'Device',
  '@capacitor/preferences':        'Preferences',
  'send-intent':                   'SendIntent',
};

const importPlugin = async (name) => {
  if (!isNative()) return null;
  // 1) Prefer the native-injected bridge proxy.
  const pluginName = _NPM_TO_PLUGIN[name];
  if (pluginName && typeof window !== 'undefined'
      && window.Capacitor?.Plugins?.[pluginName]) {
    return { [pluginName]: window.Capacitor.Plugins[pluginName] };
  }
  // 2) Fallback: dynamic import (only useful if the frontend is bundled
  //    with the plugin — currently no @capacitor/* is in frontend deps).
  try {
    const dyn = new Function('n', 'return import(n)');
    const mod = await dyn(name);
    return mod || null;
  } catch (err) {
    console.warn(`[mobileBridge] plugin ${name} unavailable:`, err?.message);
    return null;
  }
};

// ─── Secure storage (JWT + refresh + user preferences) ───────────────
const STORAGE_KEY = 'mesp_auth_state';

export const secureStorage = {
  async set(value) {
    const json = JSON.stringify(value);
    if (!isNative()) {
      localStorage.setItem(STORAGE_KEY, json);
      return;
    }
    const Preferences = await importPlugin('@capacitor/preferences');
    if (!Preferences?.Preferences) {
      localStorage.setItem(STORAGE_KEY, json);
      return;
    }
    await Preferences.Preferences.set({ key: STORAGE_KEY, value: json });
  },
  async get() {
    if (!isNative()) {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    }
    const Preferences = await importPlugin('@capacitor/preferences');
    if (!Preferences?.Preferences) {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    }
    const { value } = await Preferences.Preferences.get({ key: STORAGE_KEY });
    return value ? JSON.parse(value) : null;
  },
  async clear() {
    if (!isNative()) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    const Preferences = await importPlugin('@capacitor/preferences');
    if (Preferences?.Preferences) {
      await Preferences.Preferences.remove({ key: STORAGE_KEY });
    }
    localStorage.removeItem(STORAGE_KEY);
  },
};

// ─── Device identity ────────────────────────────────────────────────
async function _deviceId() {
  const Device = await importPlugin('@capacitor/device');
  if (!Device?.Device) return 'web-' + (window.navigator.userAgent || '').slice(0, 24);
  const info = await Device.Device.getId();
  return info.identifier || 'unknown';
}

async function _appVersion() {
  const App = await importPlugin('@capacitor/app');
  if (!App?.App) return process.env.REACT_APP_VERSION || 'web';
  try {
    const info = await App.App.getInfo();
    return info.version || '0.0.0';
  } catch {
    return process.env.REACT_APP_VERSION || 'unknown';
  }
}

// ─── FCM push registration + token lifecycle ────────────────────────
// Feb 2026 hardening (QA fork): the previous flow attached a fresh
// `registration` listener on every call and re-invoked
// `PushNotifications.register()`. On @capacitor/push-notifications v6
// the plugin does NOT re-emit the `registration` event when the FCM
// token is unchanged — so a second call (e.g. the post-login one from
// AuthContext) would wait forever, `pushRegistered` stayed `false`, and
// the token upload never happened. Device 2 in the pilot fleet hit this
// deterministically. We now:
//   • attach `registration` + `registrationError` listeners exactly
//     ONCE per app session,
//   • cache the FCM token in module state,
//   • treat upload as an independent axios POST that any subsequent
//     caller can invoke synchronously when auth is available.
let pushRegistered   = false;      // token successfully uploaded once
let _listenersReady  = false;      // idempotent listener attach guard
let _cachedFcmToken  = null;       // last FCM token received
let _lastUploadError = null;       // remembered for debug/diag callers
let _tokenWaiters    = [];         // callers waiting for the very first token

/**
 * Notification Pipeline V2 · Heads-up channel bootstrap (Feb 2026).
 *
 * MESP QA reported that Android devices did not show native heads-up
 * pop-ups. Root cause: without an explicit high-importance channel,
 * Android 8+ routes every FCM push into a default channel with
 * IMPORTANCE_DEFAULT which suppresses the heads-up class.
 *
 * We create ONE channel per app install with the following contract:
 *   * id           = "mesp_high_priority"  (matches AndroidManifest
 *                     meta-data + AndroidNotification.channel_id on
 *                     the backend so a single channel handles every
 *                     event class end-to-end).
 *   * importance   = 5  (IMPORTANCE_HIGH — required for heads-up).
 *   * visibility   = 1  (VISIBILITY_PUBLIC — shows body on lock screen).
 *   * vibration + light + sound = enabled by default.
 *
 * Idempotent: `createChannel` on Android is safe to call repeatedly
 * (the platform dedupes by id).
 */
async function _ensureHighPriorityChannel(PushNotifications) {
  try {
    if (typeof PushNotifications.createChannel !== 'function') return;
    // (1) Generic high-priority channel used by every other event
    // (messages, assignments, support). Uses the OS default sound.
    await PushNotifications.createChannel({
      id: 'mesp_high_priority',
      name: 'إشعارات MESP الفورية',
      description: 'إشعارات فورية للحصص والواجبات والرسائل والدعم',
      importance: 5,        // IMPORTANCE_HIGH → heads-up allowed
      visibility: 1,        // VISIBILITY_PUBLIC → shows on lock screen
      sound: 'default',
      vibration: true,
      lights: true,
      lightColor: '#0F172A',
    });
    // (2) Dedicated class-reminder channel — mandated by product
    // (Feb 2026 spec). Carries the custom "MESP academic soft tone"
    // (`res/raw/mesp_reminder.wav`) so students immediately recognise
    // the sound as "class starting soon". Channels are IMMUTABLE on
    // Android after first creation — DO NOT change this channel's
    // id, importance, or sound after ship. If a future change is
    // needed, mint a new id (e.g. `mesp_class_reminder_v2`).
    await PushNotifications.createChannel({
      id: 'mesp_class_reminder',
      name: 'تذكير الحصص القادمة',
      description: 'تنبيه قبل بدء الحصص المباشرة برنين MESP الأكاديمي',
      importance: 5,        // IMPORTANCE_HIGH → heads-up
      visibility: 1,        // VISIBILITY_PUBLIC
      sound: 'mesp_reminder',
      vibration: true,
      lights: true,
      lightColor: '#0F172A',
    });
    // (3-5) Urgency ladder channels (Jun 2026 — manual class alerts).
    // Teacher-triggered «alert absent students» pushes route into one of
    // these depending on the chosen alert_type (Small/Medium/Long).
    // v1.2.0 (Jun 2026) — minted fresh emergency_*_v1 ids: Android
    // channels are IMMUTABLE after first creation. Devices whose older
    // channels got created before the sound resources existed (or were
    // user-modified) were
    // permanently stuck on the DEFAULT sound — a fresh id is the ONLY
    // way to re-bind the custom ringtone without a reinstall. Per the
    // late-student alert spec ALL three are now IMPORTANCE_HIGH so the
    // full ringtone plays even on the lock screen. Backend
    // `_ALERT_TYPE_CHANNEL` (livekit_voice.py) + `ALERT_CHANNEL`
    // (auto_paging.py) target the same *_v3 ids. Old *_v2 channels are
    // left orphaned (harmless).
    await PushNotifications.createChannel({
      id: 'emergency_chime_v1',
      name: 'تنبيهات هادئة',
      description: 'تنبيهات خفيفة برنّة قصيرة (5 ثوانٍ)',
      importance: 5,        // IMPORTANCE_HIGH → full sound + heads-up
      visibility: 1,
      sound: 'mesp_chime_short',
      vibration: true,
      lights: true,
      lightColor: '#C9A24E',
    });
    await PushNotifications.createChannel({
      id: 'emergency_melody_v1',
      name: 'تنبيهات متوسطة الأهمية',
      description: 'تنبيهات بلحن أكاديمي متوسط (10 ثوانٍ)',
      importance: 5,        // IMPORTANCE_HIGH → heads-up
      visibility: 1,
      sound: 'mesp_melody_medium',
      vibration: true,
      lights: true,
      lightColor: '#C9A24E',
    });
    await PushNotifications.createChannel({
      id: 'emergency_ring_v1',
      name: 'نداء الحصة المباشرة',
      description: 'رنين أكاديمي طويل (20 ثانية) يشبه المكالمة لتنبيه الطلاب المتأخرين عن الحصة',
      importance: 5,        // IMPORTANCE_HIGH → heads-up + full ring
      visibility: 1,
      sound: 'mesp_ring_long',
      vibration: true,
      lights: true,
      lightColor: '#C9A24E',
    });
    // (6-8) v1.3.0 sonic identity — emergency_*_v2 family. On pre-1.3.0
    // APKs the new *_v2 raw files don't exist, so we bind these ids to
    // the OLD bundled sounds here: v2 pushes from the backend are never
    // dropped on any install. On v1.3.0+ the SAME ids are created
    // natively in MainActivity BEFORE the WebView loads, bound to the
    // NEW sounds — first definition wins, this call becomes a no-op.
    await PushNotifications.createChannel({
      id: 'emergency_chime_v2',
      name: 'تنبيهات هادئة',
      description: 'نداء MESP الهادئ — رنّة قصيرة راقية',
      importance: 5,
      visibility: 1,
      sound: 'mesp_chime_short',
      vibration: true,
      lights: true,
      lightColor: '#C9A24E',
    });
    await PushNotifications.createChannel({
      id: 'emergency_melody_v2',
      name: 'تنبيهات متوسطة الأهمية',
      description: 'لحن MESP المتوسط — نغمة استدعاء جادة',
      importance: 5,
      visibility: 1,
      sound: 'mesp_melody_medium',
      vibration: true,
      lights: true,
      lightColor: '#C9A24E',
    });
    await PushNotifications.createChannel({
      id: 'emergency_ring_v2',
      name: 'نداء الحصة المباشرة',
      description: 'رنين MESP الطويل — جرس الاستدعاء المهيب',
      importance: 5,
      visibility: 1,
      sound: 'mesp_ring_long',
      vibration: true,
      lights: true,
      lightColor: '#C9A24E',
    });
  } catch (err) {
    // Never fail push registration because of channel creation.
    console.warn('[mobileBridge] createChannel failed:', err?.message);
  }
}

// Internal — upload the cached FCM token to the backend. Never throws.
// `accessToken` (optional) is the freshly issued JWT passed explicitly by
// the account switcher so the upload never races a stale localStorage read.
let _explicitAccessToken = null;
async function _uploadCachedToken(axiosInstance, accessToken) {
  if (!_cachedFcmToken) {
    return { registered: false, reason: 'no_token_yet' };
  }
  try {
    const client = axiosInstance || axios;
    const bearer = accessToken || _explicitAccessToken || null;
    // 2026-07-26 · Multi-account — belt-and-braces `user_id` in the
    // payload. The backend already keys by the JWT's `sub` claim via
    // `get_current_user`, but embedding the id makes fanout debugging
    // (which account is bound to which token on this device) trivial
    // in server logs. Best-effort decode; a missing value is fine —
    // the backend ignores it.
    let userIdFromToken = null;
    try {
      const raw = bearer || window.localStorage.getItem('mesp_access_token') || '';
      const seg = raw.split('.')[1] || '';
      if (seg) {
        const b64 = seg.replace(/-/g, '+').replace(/_/g, '/');
        const pad = b64 + '='.repeat((4 - b64.length % 4) % 4);
        const payload = JSON.parse(atob(pad));
        userIdFromToken = payload?.sub || null;
      }
    } catch (_e) { /* corrupt token — ignore */ }
    await client.post(`${API}/device-tokens/register`, {
      token:       _cachedFcmToken,
      device_id:   await _deviceId(),
      platform:    (window.Capacitor?.getPlatform?.() || 'unknown').toLowerCase(),
      app_version: await _appVersion(),
      user_id:     userIdFromToken,
    }, {
      withCredentials: true,
      // Explicit bearer beats the interceptor's localStorage read.
      ...(bearer ? { headers: { Authorization: `Bearer ${bearer}` } } : {}),
    });
    pushRegistered   = true;
    _lastUploadError = null;
    return { registered: true, token_tail: _cachedFcmToken.slice(-12) };
  } catch (err) {
    _lastUploadError = err?.response?.status || err?.message || 'unknown';
    return { registered: false, reason: 'backend_error', error: _lastUploadError };
  }
}

// Internal — attach FCM plugin listeners exactly once. Subsequent calls
// short-circuit; the first token that arrives is cached and delivered
// to every pending `_tokenWaiters` promise plus every future caller.
async function _attachPushListenersOnce(PushNotifications, axiosInstance) {
  if (_listenersReady) return;
  _listenersReady = true;

  await PushNotifications.addListener('registration', async (t) => {
    _cachedFcmToken = t?.value || null;
    // Attempt an immediate upload — will 401 pre-login (fine, we retry
    // later from `AuthContext.useEffect([user])`).
    const result = await _uploadCachedToken(axiosInstance);
    // Drain any callers that were awaiting the very first token.
    const waiters = _tokenWaiters;
    _tokenWaiters = [];
    waiters.forEach(w => w(result));
  });

  await PushNotifications.addListener('registrationError', (err) => {
    _lastUploadError = err?.error || 'fcm_registration_error';
    const waiters = _tokenWaiters;
    _tokenWaiters = [];
    waiters.forEach(w => w({ registered: false, reason: 'fcm_error',
                              error: _lastUploadError }));
  });
}

export async function registerPushToken({ axiosInstance, force, accessToken } = {}) {
  if (!isNative()) return { registered: false, reason: 'web' };
  // Remember (or reset) the explicitly supplied bearer so the
  // listener-driven upload path also uses the fresh identity.
  _explicitAccessToken = accessToken || null;

  const Push = await importPlugin('@capacitor/push-notifications');
  if (!Push?.PushNotifications) {
    return { registered: false, reason: 'plugin_missing' };
  }
  const { PushNotifications } = Push;

  // Jun 2026 late-alert fix — (re)ensure notification channels on EVERY
  // app open, BEFORE any fast-path early return. Previously channels were
  // only created on the first-ever slow path, so devices that hit the
  // cached-token path after a remote web update never created newly
  // minted channel ids → FCM fell back to the manifest default channel
  // (default sound). createChannel is idempotent — safe every launch.
  await _ensureHighPriorityChannel(PushNotifications);

  // Fast path — we already uploaded the token this session.
  if (pushRegistered && !force) {
    return { registered: true, reason: 'already' };
  }

  // Fast path — token already cached from an earlier `registration`
  // event; just (re-)attempt the upload now that auth may be present.
  if (_cachedFcmToken) {
    return _uploadCachedToken(axiosInstance, accessToken);
  }

  // Slow path — first time we see this session. Ensure permission,
  // ensure channels, wire the listeners exactly once, then call
  // `Push.register()`. Return a promise that resolves as soon as the
  // FCM plugin gives us a token (or errors out).
  let permission = await PushNotifications.checkPermissions();
  if (permission.receive !== 'granted') {
    permission = await PushNotifications.requestPermissions();
  }
  if (permission.receive !== 'granted') {
    return { registered: false, reason: 'denied' };
  }

  await _attachPushListenersOnce(PushNotifications, axiosInstance);

  return new Promise((resolve) => {
    // Timeout so a caller never hangs forever if FCM is completely
    // unreachable (airplane mode, no Google Play services, etc.).
    const timer = setTimeout(() => {
      _tokenWaiters = _tokenWaiters.filter(w => w !== settle);
      resolve({ registered: false, reason: 'timeout' });
    }, 15000);
    const settle = (result) => { clearTimeout(timer); resolve(result); };
    _tokenWaiters.push(settle);
    // Kick off / retry FCM registration. Safe to call repeatedly.
    PushNotifications.register().catch((err) => {
      _tokenWaiters = _tokenWaiters.filter(w => w !== settle);
      settle({ registered: false, reason: 'register_failed',
               error: err?.message });
    });
  });
}

// Diagnostic — small dump the shell / dev-tools panel can print.
// ── Jun 2026 — Mandatory notifications gate helpers ──────────────────
// checkNotificationsPermission(): 'granted' | 'denied' | 'prompt' | 'web'.
// On new APKs (v1.2.0+) we cross-check the OS-level switch too, because
// a user can grant the runtime permission then flip notifications OFF
// from system settings — checkPermissions alone misses that.
export async function checkNotificationsPermission() {
  if (!isNative()) return { state: 'web' };
  try {
    const Push = await importPlugin('@capacitor/push-notifications');
    if (!Push) return { state: 'web' };
    const { receive } = await Push.PushNotifications.checkPermissions();
    let state = receive === 'granted' ? 'granted'
      : receive === 'denied' ? 'denied' : 'prompt';
    if (state === 'granted') {
      const native = window.Capacitor?.Plugins?.NativeAudioMode;
      if (native && typeof native.areNotificationsEnabled === 'function') {
        try {
          const { enabled } = await native.areNotificationsEnabled();
          if (enabled === false) state = 'denied';
        } catch (_e) { /* old APK — trust checkPermissions */ }
      }
    }
    return { state };
  } catch (_e) {
    return { state: 'web' };
  }
}

// Fires the native Android permission dialog (only effective while the
// permission is still in the 'prompt' state).
export async function requestNotificationsPermission() {
  if (!isNative()) return { state: 'web' };
  try {
    const Push = await importPlugin('@capacitor/push-notifications');
    if (!Push) return { state: 'denied' };
    const { receive } = await Push.PushNotifications.requestPermissions();
    return { state: receive === 'granted' ? 'granted' : 'denied' };
  } catch (_e) {
    return { state: 'denied' };
  }
}

// Deep-links into THIS app's Android notification settings page.
// Returns false on old APKs that predate the native method — the
// caller then shows manual instructions instead.
export async function openNotificationSettings() {
  if (!isNative()) return false;
  const native = window.Capacitor?.Plugins?.NativeAudioMode;
  if (native && typeof native.openNotificationSettings === 'function') {
    try {
      await native.openNotificationSettings();
      return true;
    } catch (_e) { /* fall through */ }
  }
  return false;
}


export function _debugPushState() {
  return {
    native:           isNative(),
    listenersReady:   _listenersReady,
    pushRegistered,
    cachedTokenTail:  _cachedFcmToken ? _cachedFcmToken.slice(-12) : null,
    lastUploadError:  _lastUploadError,
  };
}

export async function revokePushToken({ axiosInstance } = {}) {
  if (!isNative()) return { revoked: false, reason: 'web' };
  try {
    const client = axiosInstance || axios;
    await client.post(`${API}/device-tokens/revoke`, {
      device_id: await _deviceId(),
      reason: 'user_action',
    }, { withCredentials: true });
    pushRegistered = false;
    return { revoked: true };
  } catch (err) {
    return { revoked: false, reason: 'backend_error', error: err?.message };
  }
}

// ─── Deep-link handling ─────────────────────────────────────────────
/**
 * Register a callback invoked with the target path whenever the OS
 * routes a `mesp://` or Universal Link into the app. Accepts URLs of
 * the form:
 *    mesp://open?n=<nonce>
 *    https://mesp.<domain>/dl/<nonce>
 * and translates them to `/dl/<nonce>` so the SPA router picks it up
 * unchanged.
 *
 * The subscription is idempotent — calling twice with the same
 * callback re-registers cleanly (Capacitor allows multiple listeners
 * but we dedupe by keeping only the latest).
 */
let currentDeepLinkListener = null;

export async function subscribeToDeepLinks(navigate) {
  if (!isNative() || typeof navigate !== 'function') return () => {};

  const App = await importPlugin('@capacitor/app');
  if (!App?.App) return () => {};

  const handler = (event) => {
    try {
      const url = event?.url || '';
      let target = null;
      // Custom scheme
      if (url.startsWith('mesp://')) {
        const q = url.split('?')[1] || '';
        const params = new URLSearchParams(q);
        // Google auth round-trip: the system browser bounces the
        // Emergent session_id back via mesp://auth/callback so the
        // exchange (and the resulting cookies) happen INSIDE the app.
        const sid = params.get('session_id');
        if (url.startsWith('mesp://auth') && sid) {
          target = `/auth/callback#session_id=${encodeURIComponent(sid)}`;
        } else {
          const nonce = params.get('n');
          if (nonce) target = `/dl/${nonce}`;
        }
      }
      // Universal / App Link
      const httpsMatch = url.match(/^https?:\/\/[^/]+(\/dl\/[^?#]+)/);
      if (httpsMatch) target = httpsMatch[1];

      if (target) navigate(target);
    } catch (err) {
      console.warn('[mobileBridge] deep-link parse failed', err);
    }
  };

  // Remove previous.
  if (currentDeepLinkListener?.remove) {
    try { await currentDeepLinkListener.remove(); } catch {} // eslint-disable-line no-empty
  }
  currentDeepLinkListener = await App.App.addListener('appUrlOpen', handler);

  // Cold-start: if the app was launched (not resumed) via a deep link,
  // `appUrlOpen` never fires — the URL is only available through
  // getLaunchUrl(). Route it through the same handler ONCE — the
  // sessionStorage guard is essential because the handler triggers a
  // full page reload, which re-runs this bootstrap with the SAME
  // launch URL (infinite reload loop otherwise).
  try {
    const launch = await App.App.getLaunchUrl?.();
    const url = launch?.url || '';
    if (url.startsWith('mesp://')) {
      const KEY = 'mesp_handled_launch_url';
      let handled = null;
      try { handled = sessionStorage.getItem(KEY); } catch {} // eslint-disable-line no-empty
      if (handled !== url) {
        try { sessionStorage.setItem(KEY, url); } catch {} // eslint-disable-line no-empty
        handler({ url });
      }
    }
  } catch {} // eslint-disable-line no-empty

  return async () => {
    try { await currentDeepLinkListener?.remove(); } catch {} // eslint-disable-line no-empty
    currentDeepLinkListener = null;
  };
}

// ─── Native Android share target (Sprint 3, Feb 2026) ──────────────
/**
 * If the app was opened via the Android system share sheet the native
 * side buffers the payload; this reads it once and forwards to the
 * existing React `/share-receive` landing page. Reuses the SAME upload
 * endpoint (`POST /api/classroom-messages/{cid}/attachments`) and the
 * SAME classroom-picker UI the user already knows — no backend or
 * upload-pipeline change.
 *
 * The plugin buffers between native-launch and JS-ready, so a cold
 * start (killed → share) works identically to warm launches.
 * Additional shares while the app is running arrive as fresh payloads
 * on the next `checkSendIntentReceived` call, driven by the
 * `sendIntentReceived` event listener registered below.
 */
let currentShareListener = null;
async function _routeSharePayload(navigate, payload) {
  if (!payload) return;
  // Payload shape (send-intent v6): { title, description, type, url, additionalItems, webUrl }.
  // Jun 2026 — Universal Share Handler: NO navigation anymore (a hard
  // redirect used to yank users out of live sessions). We stash the
  // payload and fire an event; the globally-mounted
  // <ShareDestinationModal/> opens as an overlay on whatever screen
  // the user is on and owns the role-based destination routing.
  try {
    sessionStorage.setItem(
      'mesp:pending_share',
      JSON.stringify({ received_at: Date.now(), payload }),
    );
  } catch {} // eslint-disable-line no-empty
  try { window.dispatchEvent(new CustomEvent('mesp:share-received')); } catch {} // eslint-disable-line no-empty
}

export async function subscribeToShareIntents(navigate) {
  const SendIntent = await importPlugin('send-intent');
  if (!SendIntent?.SendIntent) return () => {};

  // Cold-start check (already-buffered payload)
  try {
    const initial = await SendIntent.SendIntent.checkSendIntentReceived();
    if (initial && (initial.url || initial.title || initial.description)) {
      await _routeSharePayload(navigate, initial);
    }
  } catch { /* no payload — silent */ }

  // Warm-start / while-running deliveries
  if (currentShareListener) {
    try { await currentShareListener.remove(); } catch {} // eslint-disable-line no-empty
  }
  try {
    currentShareListener = await SendIntent.SendIntent.addListener(
      'sendIntentReceived',
      async () => {
        try {
          const p = await SendIntent.SendIntent.checkSendIntentReceived();
          if (p) await _routeSharePayload(navigate, p);
        } catch {} // eslint-disable-line no-empty
      },
    );
  } catch { /* listener not supported on this plugin build */ }

  return async () => {
    try { await currentShareListener?.remove(); } catch {} // eslint-disable-line no-empty
    currentShareListener = null;
  };
}

// ─── Push-notification tap routing (Sprint 4, Feb 2026) ─────────────
/**
 * When a user taps an FCM notification, Capacitor emits
 * `pushNotificationActionPerformed` with the full notification payload
 * including our `data.link` (e.g. `/voice-room/{cid}` from the V2
 * class reminder). We forward that path to React Router via the
 * existing `navigate` callback so the tap lands the student directly
 * inside the LiveKit voice room. No new SDK, no new backend contract.
 */
let currentPushActionListener = null;

export async function subscribeToPushActions(navigate) {
  if (!isNative() || typeof navigate !== 'function') return () => {};
  const Push = await importPlugin('@capacitor/push-notifications');
  if (!Push?.PushNotifications) return () => {};

  const handler = (event) => {
    try {
      const link = event?.notification?.data?.link;
      if (link && typeof link === 'string' && link.startsWith('/')) {
        navigate(link);
      }
    } catch (err) {
      console.warn('[mobileBridge] push-tap navigate failed', err);
    }
  };

  if (currentPushActionListener?.remove) {
    try { await currentPushActionListener.remove(); } catch {} // eslint-disable-line no-empty
  }
  try {
    currentPushActionListener = await Push.PushNotifications.addListener(
      'pushNotificationActionPerformed', handler,
    );
  } catch { /* not supported on this plugin build */ }

  return async () => {
    try { await currentPushActionListener?.remove(); } catch {} // eslint-disable-line no-empty
    currentPushActionListener = null;
  };
}

/** Foreground Android notifications are delivered to JavaScript rather than
 * shown by the OS. Play the same condition-specific sound and surface a
 * global in-app alert; background/locked delivery remains OS-managed. */
export async function subscribeToForegroundLiveAlerts() {
  if (!isNative()) return () => {};
  const plugin = await importPlugin('@capacitor/push-notifications');
  if (!plugin?.PushNotifications) return () => {};
  const handle = await plugin.PushNotifications.addListener(
    'pushNotificationReceived',
    (notification) => {
      const data = notification?.data || {};
      const event = data.event;
      const soundUrl = LIVE_EMERGENCY_SOUNDS[event];
      if (!soundUrl) return;
      const key = data.notification_id || `${event}:${data.link || ''}`;
      const now = Date.now();
      if (foregroundPushSeen.has(key) && now - foregroundPushSeen.get(key) < 30000) return;
      foregroundPushSeen.set(key, now);
      try {
        const audio = new Audio(soundUrl);
        audio.volume = 1;
        audio.play().catch(() => {});
      } catch (_) { /* OS/in-app row still surfaces the alert. */ }
      try {
        window.dispatchEvent(new CustomEvent('mesp:live-emergency', {
          detail: {
            title: notification?.title || 'تنبيه حصة مباشرة',
            body: notification?.body || '',
            link: data.link || '/admin/classroom-monitor',
            event,
          },
        }));
      } catch (_) { /* WebView may be shutting down. */ }
    },
  );
  return async () => { try { await handle?.remove?.(); } catch (_) {} };
}

// ─── Android hardware back button (Jun 2026 — WhatsApp-style UX) ────
/**
 * Registers a single `backButton` listener on the Capacitor App plugin.
 * IMPORTANT: as soon as ANY listener is attached, Capacitor stops
 * performing its default behaviour (webview history back / app exit) —
 * the supplied `handler` owns the full decision tree. The React side
 * (<AndroidBackHandler/>) implements:
 *   1. close open modal/drawer first,
 *   2. in-app history back until the Dashboard,
 *   3. double-tap-to-exit on the Dashboard root,
 *   4. never return to Login/Welcome once authenticated.
 */
let currentBackButtonListener = null;

export async function subscribeToBackButton(handler) {
  if (!isNative() || typeof handler !== 'function') return () => {};
  const App = await importPlugin('@capacitor/app');
  if (!App?.App) return () => {};

  const prev = currentBackButtonListener;
  currentBackButtonListener = null;
  if (prev?.remove) {
    try { await prev.remove(); } catch {} // eslint-disable-line no-empty
  }
  const listener = await App.App.addListener('backButton', (ev) => {
    try { handler(ev); } catch (err) {
      console.warn('[mobileBridge] backButton handler failed', err);
    }
  });
  currentBackButtonListener = listener;

  return async () => {
    try { await listener.remove(); } catch {} // eslint-disable-line no-empty
    if (currentBackButtonListener === listener) currentBackButtonListener = null;
  };
}

/** Programmatic app exit — Android only (iOS forbids it). */
export async function exitApp() {
  const App = await importPlugin('@capacitor/app');
  try { await App?.App?.exitApp(); } catch {} // eslint-disable-line no-empty
}

// ─── Public bootstrap (call once from App.js on login success) ──────
export async function bootstrapMobileIntegration({ navigate, axiosInstance } = {}) {
  if (!isNative()) return { native: false };
  const push          = await registerPushToken({ axiosInstance });
  const unsubDeep     = await subscribeToDeepLinks(navigate);
  const unsubShare    = await subscribeToShareIntents(navigate);
  const unsubPushTap  = await subscribeToPushActions(navigate);
  const unsubLivePush = await subscribeToForegroundLiveAlerts();
  return {
    native: true,
    push,
    unsubscribe: async () => {
      try { await unsubDeep?.(); }    catch {} // eslint-disable-line no-empty
      try { await unsubShare?.(); }   catch {} // eslint-disable-line no-empty
      try { await unsubPushTap?.(); } catch {} // eslint-disable-line no-empty
      try { await unsubLivePush?.(); } catch {} // eslint-disable-line no-empty
    },
  };
}
